/**
 * AdminConfigPage (FND-007, H1).
 *
 * Read-only view of the tenant's configurable rule sets (GET /rules) plus a
 * non-mutating 'simulate rule' panel (POST /rules/{id}/simulate). Simulation
 * dry-runs a chosen version against operator-supplied input facts and renders
 * the explainable decision + factor breakdown — without writing a rule_eval_log.
 */
import { useMemo, useState } from 'react';
import { LoadingIndicator } from '../components/LoadingIndicator';
import { PageHeader } from '../components/PageHeader';
import { Card } from '../components/Card';
import {
  useRuleSets,
  useRuleSimulation,
  type RuleSet,
} from '../hooks/useRules';

/** Default JSON context shown in the simulate editor as a starting point. */
const DEFAULT_CONTEXT = JSON.stringify(
  { charge_level: 3, days_since_admission: 12, state: 'TX' },
  null,
  2
);

function RuleSetTable({
  ruleSets,
  selectedId,
  onSelect,
}: {
  ruleSets: RuleSet[];
  selectedId: string | null;
  onSelect: (rs: RuleSet) => void;
}) {
  return (
    <table style={styles.table}>
      <thead>
        <tr>
          <th style={styles.th}>Rule set</th>
          <th style={styles.th}>Decision type</th>
          <th style={styles.thRight}>Active version</th>
          <th style={styles.th}></th>
        </tr>
      </thead>
      <tbody>
        {ruleSets.map((rs) => (
          <tr
            key={rs.rule_set_id}
            style={rs.rule_set_id === selectedId ? styles.rowActive : undefined}
          >
            <td style={styles.td}>
              <div style={{ fontWeight: 500 }}>{rs.name}</div>
              {rs.description ? <div style={styles.muted}>{rs.description}</div> : null}
            </td>
            <td style={styles.td}>{rs.decision_type.replace(/_/g, ' ')}</td>
            <td style={styles.tdRight}>v{rs.active_version}</td>
            <td style={styles.td}>
              <button type="button" onClick={() => onSelect(rs)} style={styles.smallBtn}>
                Simulate
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function AdminConfigPage() {
  const { ruleSets, isLoading, error, refresh } = useRuleSets();
  const sim = useRuleSimulation();

  const [selected, setSelected] = useState<RuleSet | null>(null);
  const [version, setVersion] = useState<number>(1);
  const [contextText, setContextText] = useState<string>(DEFAULT_CONTEXT);
  const [parseError, setParseError] = useState<string | null>(null);

  const handleSelect = (rs: RuleSet) => {
    setSelected(rs);
    setVersion(rs.active_version);
    sim.clear();
  };

  const handleSimulate = async () => {
    if (!selected) return;
    setParseError(null);
    let context: Record<string, unknown>;
    try {
      const parsed = JSON.parse(contextText) as unknown;
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        throw new Error('Context must be a JSON object.');
      }
      context = parsed as Record<string, unknown>;
    } catch (e: unknown) {
      setParseError(e instanceof Error ? e.message : 'Invalid JSON.');
      return;
    }
    await sim.simulate(selected.rule_set_id, { context, version });
  };

  const decisionEntries = useMemo(
    () => (sim.result ? Object.entries(sim.result.decision) : []),
    [sim.result]
  );

  return (
    <>
      <PageHeader
        title="Admin · Rule Configuration"
        subtitle="Read-only view of configurable rule sets with an explainable simulation panel (FND-007, H1). Simulations are dry-runs and are never logged."
      />

      {/* Rule set catalog */}
      <Card
        title="Rule sets"
        actions={
          <button type="button" className="btn btn-secondary btn-sm" onClick={refresh} disabled={isLoading}>
            Refresh
          </button>
        }
        style={{ marginBottom: 20 }}
      >
        {isLoading && <LoadingIndicator text="Loading rule sets…" />}
        {error && (
          <p style={styles.error} role="alert">
            {error.message}
          </p>
        )}
        {!isLoading && ruleSets.length === 0 && !error && (
          <p style={styles.empty}>No rule sets configured for this tenant.</p>
        )}
        {ruleSets.length > 0 && (
          <RuleSetTable ruleSets={ruleSets} selectedId={selected?.rule_set_id ?? null} onSelect={handleSelect} />
        )}
      </Card>

      {/* Simulate panel */}
      <Card title="Simulate rule">
        {!selected ? (
          <p style={styles.empty}>Choose a rule set above to simulate.</p>
        ) : (
          <div style={styles.simBody}>
            <div style={styles.simControls}>
              <div style={styles.field}>
                <label style={styles.label}>Rule set</label>
                <div style={styles.readonlyValue}>
                  {selected.name} <span style={styles.muted}>({selected.rule_set_id})</span>
                </div>
              </div>
              <div style={styles.field}>
                <label style={styles.label} htmlFor="sim-version">
                  Version
                </label>
                <input
                  id="sim-version"
                  type="number"
                  min={1}
                  value={version}
                  onChange={(e) => setVersion(Math.max(1, Number(e.target.value) || 1))}
                  style={styles.versionInput}
                />
              </div>
              <div style={styles.field}>
                <label style={styles.label} htmlFor="sim-context">
                  Input facts (JSON)
                </label>
                <textarea
                  id="sim-context"
                  value={contextText}
                  onChange={(e) => setContextText(e.target.value)}
                  rows={8}
                  spellCheck={false}
                  style={styles.textarea}
                />
              </div>
              {parseError && (
                <p style={styles.error} role="alert">
                  {parseError}
                </p>
              )}
              {sim.error && (
                <p style={styles.error} role="alert">
                  {sim.error.message}
                </p>
              )}
              <button
                type="button"
                onClick={handleSimulate}
                disabled={sim.isSimulating}
                style={styles.primaryBtn}
              >
                Run simulation
              </button>
              {sim.isSimulating && <LoadingIndicator text="Simulating…" />}
            </div>

            <div style={styles.simResult}>
              <h3 style={styles.sectionLabel}>Result</h3>
              {!sim.result ? (
                <p style={styles.empty}>Run a simulation to see the explainable output.</p>
              ) : (
                <>
                  <p style={styles.resultMeta}>
                    Applied <strong>v{sim.result.rule_version}</strong> ·{' '}
                    {sim.result.decision_type.replace(/_/g, ' ')}
                  </p>
                  <h4 style={styles.subLabel}>Decision</h4>
                  <dl style={styles.decisionGrid}>
                    {decisionEntries.map(([k, v]) => (
                      <div key={k} style={styles.decisionRow}>
                        <dt style={styles.dt}>{k}</dt>
                        <dd style={styles.dd}>{String(v)}</dd>
                      </div>
                    ))}
                  </dl>
                  {sim.result.explanation && (
                    <p style={styles.explanation}>{sim.result.explanation}</p>
                  )}
                  <h4 style={styles.subLabel}>Factor breakdown</h4>
                  {sim.result.factor_breakdown.length === 0 ? (
                    <p style={styles.empty}>No factor breakdown.</p>
                  ) : (
                    <table style={styles.table}>
                      <thead>
                        <tr>
                          <th style={styles.th}>Factor</th>
                          <th style={styles.thRight}>Value</th>
                          <th style={styles.thRight}>Weight</th>
                          <th style={styles.thRight}>Contribution</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sim.result.factor_breakdown.map((f) => (
                          <tr key={f.name}>
                            <td style={styles.td}>
                              <div style={{ fontWeight: 500 }}>{f.label}</div>
                              {f.detail ? <div style={styles.muted}>{f.detail}</div> : null}
                            </td>
                            <td style={styles.tdRight}>{String(f.value)}</td>
                            <td style={styles.tdRight}>{f.weight}</td>
                            <td style={styles.tdRight}>{f.contribution}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </Card>
    </>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: { padding: 24, maxWidth: 1080, margin: '0 auto', width: '100%' },
  h1: { margin: '0 0 4px', fontSize: '1.5rem' },
  subtitle: { margin: '0 0 20px', color: 'var(--text-secondary)' },
  panel: {
    background: '#FFFFFF',
    border: '1px solid var(--border)',
    borderRadius: 8,
    padding: 20,
    marginBottom: 20,
    boxShadow: 'var(--shadow-sm)',
  },
  panelHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  panelTitle: { margin: '0 0 12px', fontSize: '1.1rem' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' },
  th: { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid var(--border)', color: 'var(--text-secondary)', fontWeight: 600 },
  thRight: { textAlign: 'right', padding: '8px 10px', borderBottom: '2px solid var(--border)', color: 'var(--text-secondary)', fontWeight: 600 },
  td: { padding: '10px', borderBottom: '1px solid var(--border)', verticalAlign: 'top' },
  tdRight: { padding: '10px', borderBottom: '1px solid var(--border)', textAlign: 'right', verticalAlign: 'top' },
  rowActive: { background: 'var(--primary-light)' },
  simBody: { display: 'grid', gridTemplateColumns: 'minmax(280px, 380px) 1fr', gap: 24 },
  simControls: { display: 'flex', flexDirection: 'column', gap: 12 },
  simResult: { minHeight: 120 },
  field: { display: 'flex', flexDirection: 'column', gap: 4 },
  label: { fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary)' },
  readonlyValue: { padding: '8px 0', fontWeight: 500 },
  versionInput: {
    width: 100,
    padding: '8px 10px',
    border: '1px solid var(--border)',
    borderRadius: 6,
    fontSize: '0.95rem',
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid var(--border)',
    borderRadius: 6,
    fontFamily: 'monospace',
    fontSize: '0.85rem',
    resize: 'vertical',
  },
  primaryBtn: {
    alignSelf: 'flex-start',
    padding: '9px 18px',
    background: 'var(--primary)',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 6,
    fontWeight: 600,
    cursor: 'pointer',
  },
  smallBtn: {
    padding: '5px 12px',
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 5,
    fontSize: '0.82rem',
    cursor: 'pointer',
  },
  sectionLabel: { fontSize: '0.86rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-secondary)', margin: '0 0 8px' },
  subLabel: { fontSize: '0.92rem', margin: '14px 0 6px' },
  resultMeta: { margin: '0 0 8px', color: 'var(--text-primary)' },
  decisionGrid: { display: 'flex', flexDirection: 'column', gap: 4, margin: 0 },
  decisionRow: { display: 'flex', gap: 12, alignItems: 'baseline' },
  dt: { color: 'var(--text-secondary)', fontSize: '0.86rem', minWidth: 160 },
  dd: { margin: 0, fontWeight: 600 },
  explanation: { margin: '8px 0', color: 'var(--text-secondary)', fontStyle: 'italic' },
  muted: { color: 'var(--text-secondary)', fontSize: '0.82rem' },
  error: { color: 'var(--danger, #B91C1C)', fontSize: '0.9rem' },
  empty: { color: 'var(--text-secondary)', fontSize: '0.9rem' },
};
